/* Copyright Information: Created by BuyGroup */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Card, CardBody, Button, Spinner, Input } from "reactstrap";
import Breadcrumbs from "../components/common/breadcrumb";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import Moment from "react-moment";
import Select from "react-select";
import "./Quotes.css";
import { Link } from "react-router-dom";
import { formatPhoneNumber } from "../utils";
import { listQuotes } from "../services/QuoteServices";
import { toast } from "react-toastify";

// Get status display name
const getStatusDisplayName = (status) => {
  const statusNames = {
    submitted: "Quote Requested",
    sent_to_supplier: "Sent to Supplier",
    supplier_quote_received: "Supplier Quote Received",
    assigned_to_customer: "Quote sent to customer",
    order_placed: "Order Placed",
    declined: "Declined",
    expired: "Expired",
    order_failed: "Order Failed",
  };
  return statusNames[status] || status;
};

// Status formatter
const statusFormatter = (cell, row) => {
  return (
    <div className={`status_column quote-status ${row.status}`}>
      {getStatusDisplayName(row.status)}
    </div>
  );
};

// Date formatter
const dateFormatter = (cell, row) => {
  return <Moment format="MM/DD/YYYY">{cell}</Moment>;
};

// Actions formatter
const actionsFormatter = (cell, row) => {
  return (
    <Link to={{ pathname: "quotes/view/" + row.quote_id }}>
      <Button color="primary" size="sm" className="view-quote-btn">
        View
      </Button>
    </Link>
  );
};

const columns = [
  {
    dataField: "quote_id",
    text: "Quote ID",
    headerClasses: "header-classes-quote-id",
  },
  {
    dataField: "name",
    text: "Customer",
    headerClasses: "header-classes-customer-name",
  },
  {
    dataField: "phone_number",
    text: "Phone",
    headerAlign: "left",
    align: "left",
    formatter: (cell, row) => {
      return formatPhoneNumber(cell);
    },
    headerClasses: "header-classes-phone",
  },
  {
    dataField: "email",
    text: "Email",
    headerStyle: {
      width: "15%",
    },
  },
  {
    dataField: "start_date",
    text: "Date",
    formatter: dateFormatter,
    headerStyle: {
      width: "120px",
    },
  },
  {
    dataField: "item_count",
    text: "Items",
    headerAlign: "center",
    align: "center",
    headerStyle: {
      width: "80px",
    },
  },
  {
    dataField: "status",
    text: "Status",
    headerAlign: "center",
    headerClasses: "header-classes-status_column",
    classes: "column-classes-status_column",
    align: "center",
    formatter: statusFormatter,
    headerStyle: {
      width: "180px",
    },
  },
  {
    dataField: "actions",
    text: "Actions",
    headerAlign: "center",
    align: "center",
    formatter: actionsFormatter,
    headerClasses: "header-classes-action",
    headerStyle: {
      width: "100px",
    },
  },
];

const QuotesListing = () => {
  const [quotesList, setQuotesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchQuoteId, setSearchQuoteId] = useState("");

  // Status options for filter dropdown
  const statusOptions = [
    { label: "Quote Requested", value: "submitted" },
    { label: "Sent to Supplier", value: "sent_to_supplier" },
    { label: "Supplier Quote Received", value: "supplier_quote_received" },
    { label: "Quote sent to customer", value: "assigned_to_customer" },
    { label: "Order Placed", value: "order_placed" },
    { label: "Declined", value: "declined" },
    { label: "Expired", value: "expired" },
    { label: "Order Failed", value: "order_failed" },
  ];

  // Filter quotes based on selected status and quote_id search
  const filteredQuotesList = useMemo(() => {
    let filtered = quotesList;

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(
        (quote) => quote.status === selectedStatus.value
      );
    }

    // Filter by quote_id
    if (searchQuoteId) {
      filtered = filtered.filter((quote) => {
        const quoteId = quote.quote_id
          ? quote.quote_id.toString().toLowerCase()
          : "";
        return quoteId.includes(searchQuoteId.toLowerCase());
      });
    }

    return filtered;
  }, [quotesList, selectedStatus, searchQuoteId]);

  const handleList = useCallback(() => {
    setIsLoading(true);
    listQuotes()
      .then((res) => {
        console.log("Quotes fetched", res.data);
        setQuotesList(res.data);
      })
      .catch((err) => {
        console.error("Error fetching quotes", err);
        toast.error("Failed to fetch quotes");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    handleList();
  }, [handleList]);

  return (
    <>
      <Breadcrumbs title="Get a Quote" parent="" subtitle="" />
      <div className="container-fluid">
        <Row className="row">
          <Col md="12">
            <React.Fragment>
              <Row>
                <Col lg="12" md="12">
                  <Card className="card-box">
                    <div className="card-header">
                      <h5>Customer Quotes</h5>
                    </div>
                    <CardBody>
                      <ToolkitProvider
                        keyField="id"
                        data={filteredQuotesList}
                        columns={columns}
                      >
                        {(props) => (
                          <>
                            <Row className="table_SearchBar select_dropdown mb-3">
                              <Col
                                lg={3}
                                md={4}
                                sm={6}
                                xs={12}
                                className="filter-container-col"
                              >
                                <Select
                                  value={selectedStatus}
                                  onChange={(value) => setSelectedStatus(value)}
                                  options={statusOptions}
                                  isClearable={true}
                                  placeholder="Filter By Status"
                                />
                              </Col>
                              <Col
                                lg={3}
                                md={4}
                                sm={6}
                                xs={12}
                                className="filter-container-col"
                              >
                                <div className="product_search">
                                  <Input
                                    type="search"
                                    value={searchQuoteId}
                                    onChange={(e) =>
                                      setSearchQuoteId(e.target.value)
                                    }
                                    placeholder="Search by Quote ID"
                                    className="form-control"
                                  />
                                </div>
                              </Col>
                            </Row>
                            {isLoading ? (
                              <div className="custom-loader">
                                <Spinner animation="border" />
                              </div>
                            ) : (
                              <BootstrapTable
                                {...props.baseProps}
                                keyField="id"
                                key={`QuotesListTable`}
                                bordered={false}
                                data={filteredQuotesList}
                                columns={columns}
                                noDataIndication={"No quotes available"}
                                pagination={paginationFactory({
                                  sizePerPage: 10,
                                  showTotal: true,
                                  hideSizePerPage: true,
                                })}
                              />
                            )}
                          </>
                        )}
                      </ToolkitProvider>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </React.Fragment>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default QuotesListing;
/* Copyright Information: Created by BuyGroup */
